import { useEffect, useState, useRef, useContext } from 'react'
import jsLogger from 'js-logger'

import {
  conversationStart,
  getNavigatorLanguage
} from 'controllers/main'
import Conversation from 'pages/Conversation'
import Recap from 'pages/RecapV2'
import { TwoFactorT } from 'types/twoFactor'
import { getBackClient } from 'shared/controllers/back'
import { DBT, SessionType } from 'types/internal'
import SessionTimedout from 'pages/SessionTimedout'
import UserClickDataProvider from 'shared/contexts/UserClickDataProvider'
import StreamDataProvider, {
  PermissionErrorType
} from 'shared/contexts/StreamDataProvider'
import EndConversation from 'shared/components/EndConversation'
import ErrorPage from 'pages/ErrorPage'
import LocalizationContext from 'shared/contexts/LocalizationContext'
import ConnectionContextDataProvider from 'shared/contexts/ConnectionContextDataProvider'
import FixPermission from 'shared/components/FixPermission'
import { SessionInfo } from 'types/back'
import { ConversationStartResponse } from 'types/twoFactorProto'

const defaultAvatar = 'heygen'

const SESSION_TIMEOUT = 600000

const App = () => {
  const { setLocalization } = useContext(LocalizationContext)
  const [initializing, setInitializing] = useState(true)
  const [interactionId, setInteractionId] = useState<string>()
  const [isComplete, setIsComplete] = useState(false)
  const [initVersion, setInitVersion] = useState<0 | 1>(0)
  const [isTest, setIsTest] = useState(false)
  const [lang, setLang] = useState('en')
  const [sessionTimedout, setSessionTimedout] = useState(false)
  const sessionTimerRef = useRef<number>(0)
  const [closed, setClosed] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [permissionsError, setPermissionsError] =
    useState<PermissionErrorType | null>(null)
  const [recapAnimationOn, setRecapAnimationOn] = useState(true)
  const [conversationInfo, setConversationInfo] =
    useState<ConversationStartResponse>()

  useEffect(() => {
    let locale
    const windowUrl = new URL(window.location.href)
    const urlLang = windowUrl.searchParams.get('lang')
    if (urlLang) {
      locale = urlLang
    } else {
      const browserLang = getNavigatorLanguage().slice(0, 2).toLowerCase()
      locale = browserLang
    }
    if (locale) {
      setLang(locale)
    }
    sessionTimerRef.current = window.setTimeout(
      () => setSessionTimedout(true),
      SESSION_TIMEOUT
    )
  }, [])

  const getVariant = () => {
    const url = new URL(window.location.href)
    const v = url.searchParams.get('variant')
    switch (v) {
      case '2':
        return 'OpenAI'
      case '3':
        return 'Custom'
      default:
        return 'LlmSystem'
    }
  }

  useEffect(() => {
    const url = new URL(window.location.href)
    const initV = url.searchParams.get('init')
    const recap = url.searchParams.get('recap')
    const test = url.searchParams.get('test')
    if (test) {
      setIsTest(true)
    }
    const urlLang = url.searchParams.get('lang')
    const variant: TwoFactorT.VariantT = getVariant()

    jsLogger.debug('session start', { url, variant })

    if (recap) {
      setIsComplete(true)
      setInteractionId(recap)
      setInitializing(false)
      setRecapAnimationOn(false)
    } else {
      if (initV && initV === 'v1') {
        setInitVersion(1)
      }
      const run = async () => {
        jsLogger.log('conversationStart api call start')
        const convStartTime = Date.now()
        const apires = await conversationStart(variant, urlLang)
        jsLogger.debug('conversation started', {
          latency: Date.now() - convStartTime
        })
        if (apires) {
          if (apires.interactionId) {
            const isLocalhost =
              window.location.hostname === 'localhost' ||
              window.location.hostname === '127.0.0.1'
            const sessionInfo: SessionInfo = {
              sessionId: apires.interactionId,
              sessionType: SessionType.DEMO2,
              isLocalhost
            }
            getBackClient().start(sessionInfo)

            jsLogger.log('conversationStart interactionId', {
              interactionId: apires.interactionId
            })
            setInteractionId(apires.interactionId)
          }
          setConversationInfo(apires)
          apires.lang && setLang(apires.lang)
          apires.localization && setLocalization(apires.localization)
        }

        setInitializing(false)
      }
      run()
    }
  }, [])

  const onConversationFinished = () => {
    jsLogger.log('onConverstionFinished')
    if (interactionId) {
      const url = new URL(window.location.href)
      jsLogger.debug('conversation finished', { interactionId })
      url.searchParams.set('recap', interactionId)
      if (window.history?.replaceState) {
        window.history.replaceState(window.history.state, '', url)
      }
      getBackClient().disconnect()
      setTimeout(() => {
        setIsComplete(true)
        // window.location.href = url.toString()
      }, 500) // Add 1 second timeout before reloading
    }

    // url.searchParams.set('recap', interactionId || '')
    // setTimeout(() => {
    //   window.location.href = url.toString()
    // }, 1000) // Add 1 second timeout before reloading
  }

  const onChangeLang = (l: string) => {
    jsLogger.debug('onChangeLang', { lang: l })
    if (interactionId) {
      getBackClient()
        .demo2SetLang(l)
        .catch((e) => jsLogger.warn('demo2SetLang failed', e))
      setLang(l)
    }
  }

  const onClosed = () => {
    setClosed(true)
    clearTimeout(sessionTimerRef.current)
  }

  const renderContent = () => {
    if (permissionsError) {
      return <FixPermission />
    } else if (error !== null) {
      return <ErrorPage error={error} />
    } else if (closed) {
      return <EndConversation />
    } else if (initializing) {
      return null
    } else if (isComplete && interactionId) {
      return (
        <Recap
          interactionId={interactionId}
          isTest={isTest}
          locale={lang}
          animationOn={recapAnimationOn}
        />
      )
    } else if (sessionTimedout) {
      return <SessionTimedout locale={lang} />
    } else if (conversationInfo) {
      return (
        <ConnectionContextDataProvider>
          <StreamDataProvider
            onError={onError}
            isLazy
            onPermissionsError={setPermissionsError}
          >
            <Conversation
              conversationInfo={conversationInfo}
              onConversationFinished={onConversationFinished}
              initVersion={initVersion}
              isTest={isTest}
              lang={lang}
              onChangeLang={(l: string) => onChangeLang(l)}
              onClosed={onClosed}
              onError={onError}
            />
          </StreamDataProvider>
        </ConnectionContextDataProvider>
      )
    } else {
      return null
    }
    // } else if (emailSent) {
    //   return <EmailSent email={emailSent} locale={lang} />
    // } else {
    //   return (
    //     <EnterEmail
    //       onComplete={(email: string) => setEmailSent(email)}
    //       variant={variant}
    //       model={model}
    //       isTest={isTest}
    //       avatar={avatar}
    //       locale={lang}
    //     />
    //   )
    // }
  }

  const onError = (er: string) => {
    jsLogger.error('on error: ', er)
    setError(er)
  }

  return (
    <UserClickDataProvider>
      <div className='w-full h-full relative flex flex-col justify-center items-center no-scrollbar'>
        {renderContent()}
      </div>
    </UserClickDataProvider>
  )
}

export default App
